/**
 * 資料視覺化色盤
 *
 * 這組色盤跑過 colorblind / 對比檢核（OKLab ΔE，adjacent pairlist），
 * light 跑在 antd card 表面 #ffffff、dark 跑在 #141414：
 *   light 8 slot: CVD ΔE 9.1 / normal ΔE 19.6 / 3 色低於 3:1 → 需要 table view 或直接標籤
 *   dark  8 slot: CVD ΔE 8.4 / normal ΔE 19.3 / 全部 ≥ 3:1
 *   scatter（all-pairs）只能用前 3 slot，第 4 個 slot 會讓黃橘同時出現而失效
 *
 * slot 的「順序」本身就是安全機制，不是裝飾 — 不要重排、不要在第 8 個之後補色，
 * 超過就折成「其他」。
 */

export const CATEGORICAL_LIGHT = [
  "#2a78d6", // 1 blue
  "#eb6834", // 2 orange
  "#1baf7a", // 3 aqua
  "#eda100", // 4 yellow
  "#e87ba4", // 5 magenta
  "#008300", // 6 green
  "#4a3aa7", // 7 violet
  "#e34948", // 8 red
] as const;

export const CATEGORICAL_DARK = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
] as const;

/** 相鄰配對安全的上限（bar / line / stacked） */
export const SERIES_CAP_ADJACENT = 8;
/** 全配對安全的上限（scatter / bubble — 任兩色都可能相鄰） */
export const SERIES_CAP_ALL_PAIRS = 3;

/** 單一色相、淺→深，用在連續量值 */
export const SEQUENTIAL_BLUE = [
  "#cde2fb",
  "#b7d3f6",
  "#9ec5f4",
  "#86b6ef",
  "#6da7ec",
  "#5598e7",
  "#3987e5",
  "#2a78d6",
  "#256abf",
  "#1c5cab",
  "#184f95",
  "#104281",
  "#0d366b",
] as const;

export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;

/**
 * UI 與圖表共用的中性色。淺色的值對齊 sfc-app-tstack（apps/web）：
 * 版面底色 #ececec、header／Sider／卡片是白的、文字與框線用 antd 的中性階
 * （#1f1f1f / #595959 / #8c8c8c / #d9d9d9）。
 * surface 兩個值是色盤檢核的基準，不能改。
 */
export const CHROME = {
  light: {
    surface: "#ffffff",
    plane: "#ececec",
    /** 畫布檯面，跟 warRoom.css 的 --canvas-bg 一致（匯出 PNG 的底色） */
    canvas: "#fafafa",
    textPrimary: "#1f1f1f",
    textSecondary: "#595959",
    muted: "#8c8c8c",
    gridline: "#ececec",
    axis: "#d9d9d9",
    deEmphasis: "#bfbfbf",
  },
  dark: {
    surface: "#141414",
    plane: "#0a0a0a",
    canvas: "#1a1a1a",
    textPrimary: "#ffffff",
    textSecondary: "#a6a6a6",
    muted: "#737373",
    gridline: "#303030",
    axis: "#424242",
    deEmphasis: "#595959",
  },
} as const;

export type Mode = keyof typeof CHROME;

export const categorical = (mode: Mode): readonly string[] =>
  mode === "dark" ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;

export const chrome = (mode: Mode) => CHROME[mode];

/**
 * 依 slot 順序取色，永不循環。超過上限回傳 null，呼叫端要折成「其他」。
 */
export const seriesColor = (
  mode: Mode,
  index: number,
  cap: number = SERIES_CAP_ADJACENT,
): string | null => {
  if (index >= cap) return null;
  return categorical(mode)[index] ?? null;
};
